import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RRule } from 'rrule';
import { formatTaskDateTime } from '@/modules/common/helpers/taskDateTime.ts';
import { useGetTaskQuery } from '@/modules/common/queries/tasks.ts';
import {
  TaskFormValues,
  taskValidationSchema,
} from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import { TaskModal } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { sanitizeTaskReminders } from '@/modules/admin-dashboard/helpers/sanitizeTaskReminders.ts';
import {
  useUpdateTaskMutation,
  useUpdateTaskPartiallyMutation,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { Avatar, BaseModalProps } from 'ui';
import { ReminderMode, TaskPriority, TaskWithRelationsResponse, User } from 'compliance-api';

interface UpdateTaskModalProps extends BaseModalProps {
  id: number;
  defaultDisabled?: boolean;
  clientId?: number;
}

const initialValues: TaskFormValues = {
  name: '',
  priority: TaskPriority.Normal,
  start_date: '',
  first_due_date: '',
  recurrency_rule: '',
  user_ids: [],
  users_overdue_subs: [],
  users_completed_subs: [],
  department_ids: [],
  is_every_employee_needs_to_complete: false,
  form_id: 0,
  description: '',
  due_remainders: [],
  incident_result_id: undefined,
  client_id: undefined,
  preservedFullOptions: {
    department_ids: [],
    user_ids: [],
    users_completed_subs: [],
    users_overdue_subs: [],
    client_id: undefined,
  },
};

const getNonReminderSnapshot = (values: TaskFormValues) => {
  const recurrency_rule =
    values.recurrency_rule && values.start_date
      ? new RRule({
          ...RRule.fromString(values.recurrency_rule).origOptions,
          dtstart: dayjs.utc(values.start_date).toDate(),
        }).toString()
      : values.recurrency_rule;
  const taskValues = {
    ...values,
    recurrency_rule,
    due_remainders: undefined,
    preservedFullOptions: undefined,
  };

  return JSON.stringify({
    ...taskValues,
    user_ids: [...(taskValues.user_ids || [])].sort((a, b) => a - b),
    users_overdue_subs: [...(taskValues.users_overdue_subs || [])].sort((a, b) => a - b),
    users_completed_subs: [...(taskValues.users_completed_subs || [])].sort((a, b) => a - b),
    department_ids: [...(taskValues.department_ids || [])].sort((a, b) => a - b),
  });
};

export const createOptionsFromUser = (users: User[]) => {
  return users.map(item => {
    return {
      value: item.id,
      label: item?.first_name + ' ' + item?.last_name,
      avatar: (
        <Avatar
          withBorder
          size="sm"
          className="border-[#C4C4C4]"
          classNameLetters="!text-[12px]"
          src={item?.avatar_url}
          firstName={item?.first_name}
          lastName={item?.last_name}
        />
      ),
    };
  });
};

export const UpdateTaskModal = ({
  id,
  open,
  onClose,
  defaultDisabled = false,
  clientId,
}: UpdateTaskModalProps) => {
  const { data: task, isLoading, refetch } = useGetTaskQuery(id);
  const [disabledMode, setDisabledMode] = useState(defaultDisabled);
  const initialNonReminderSnapshotRef = useRef<string>();

  const { mutateAsync: updateTask, isLoading: isUpdating } = useUpdateTaskMutation();
  const { mutateAsync: updateTaskPartially } = useUpdateTaskPartiallyMutation();

  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };

  const isPastDate = (date: string) => date && dayjs(date).isBefore(dayjs(), 'day');
  const originalStartDate = task?.task.start_date;
  const originalDueDate = task?.task.first_due_date;
  const currentStartDate = task?.task.current_start_date ?? originalStartDate;
  const currentDueDate = task?.task.overdue_date ?? originalDueDate;

  const handleSubmit = useCallback(
    async (values: TaskFormValues) => {
      if (isPastDate(task?.task.start_date || values.start_date)) {
        const due_remainders = sanitizeTaskReminders({ ...values, id }).due_remainders;
        await updateTaskPartially({ id, due_remainders });
        await refetch();
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { preservedFullOptions, ...taskData } = values;
      await updateTask(sanitizeTaskReminders({ ...taskData, id }));
    },
    [id, refetch, task, updateTask, updateTaskPartially],
  );

  const formik = useFormik({
    initialValues: {
      ...initialValues,
    },
    validationSchema: taskValidationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    enableReinitialize: true,

    onSubmit: async values => {
      if (isPastDate(task?.task.start_date || values.start_date)) {
        if (getNonReminderSnapshot(values) !== initialNonReminderSnapshotRef.current) {
          formik.setFieldError(
            'start_date',
            `Task dates cannot be changed after the first occurrence on ${formatTaskDateTime(
              originalStartDate,
            )}.`,
          );
          return;
        }

        formik.setFieldError('start_date', undefined);
        await handleSubmit(values);
        handleCloseModal();
        return;
      }

      if (isPastDate(values.first_due_date)) {
        formik.setFieldError('first_due_date', 'Due date cannot be in the past');
        return;
      }

      await handleSubmit(values);
      handleCloseModal();
    },
  });

  useEffect(() => {
    if (task) {
      formik.setFieldValue('name', task?.task.name);
      formik.setFieldValue('priority', task?.task.priority);
      formik.setFieldValue('start_date', task?.task.start_date);
      formik.setFieldValue('first_due_date', task?.task.first_due_date);
      formik.setFieldValue('recurrency_rule', task?.task.recurrency_rule);
      formik.setFieldValue(
        'user_ids',
        task?.task.users ? task?.task.users.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'users_overdue_subs',
        task?.task.overdue_subs ? task?.task.overdue_subs.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'users_completed_subs',
        task?.task.complete_subs ? task?.task.complete_subs.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'department_ids',
        task?.task.departments ? task.task.departments.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'is_every_employee_needs_to_complete',
        task?.task.is_every_employee_needs_to_complete,
      );
      formik.setFieldValue('form_id', task?.task.form_id);
      formik.setFieldValue('incident_result_id', task?.task.incident_result_id);
      formik.setFieldValue('description', task?.task.description);
      formik.setFieldValue(
        'due_remainders',
        task?.task.due_remainders?.map(reminder => ({
          ...reminder,
          mode: reminder.mode ?? ReminderMode.Recurring,
        })),
      );

      // Use clientId from page context if provided, otherwise use task's client.id
      const taskData = task.task as TaskWithRelationsResponse & { client_id?: number };
      const effectiveClientId = clientId ?? taskData.client?.id;
      // Determine client option for the select
      let clientOption;
      if (taskData.client) {
        clientOption = {
          value: taskData.client.id,
          label: taskData.client.name,
        };
      } else {
        clientOption = undefined;
      }

      formik.setFieldValue('preservedFullOptions', {
        department_ids: (task?.task?.departments || []).map(item => {
          return { value: item.id, label: item.name };
        }),
        user_ids: createOptionsFromUser(task?.task?.users || []),
        users_completed_subs: createOptionsFromUser(task?.task?.complete_subs || []),
        users_overdue_subs: createOptionsFromUser(task?.task?.overdue_subs || []),
        client_id: clientOption,
      });

      // Set client_id AFTER preservedFullOptions to ensure it's not overwritten
      formik.setFieldValue('client_id', effectiveClientId);

      initialNonReminderSnapshotRef.current = getNonReminderSnapshot({
        ...initialValues,
        name: task.task.name,
        priority: task.task.priority,
        start_date: task.task.start_date,
        first_due_date: task.task.first_due_date,
        recurrency_rule: task.task.recurrency_rule,
        user_ids: task.task.users?.map(item => item.id) || [],
        users_overdue_subs: task.task.overdue_subs?.map(item => item.id) || [],
        users_completed_subs: task.task.complete_subs?.map(item => item.id) || [],
        department_ids: task.task.departments?.map(item => item.id) || [],
        is_every_employee_needs_to_complete: task.task.is_every_employee_needs_to_complete,
        form_id: task.task.form_id,
        incident_result_id: task.task.incident_result_id,
        description: task.task.description,
        client_id: effectiveClientId,
      });
    }
  }, [task, clientId]);

  return (
    <TaskModal
      isUpdating
      defaultForm={
        task?.task.form ? { value: task?.task.form.id, label: task?.task.form.name } : null
      }
      defaultIncident={
        task?.task.incident_result
          ? { value: { id: task?.task.incident_result.id }, label: task?.task.incident_result.name }
          : null
      }
      disabledMode={disabledMode}
      setDisabledMode={setDisabledMode}
      isEveryBodyNeedsToBeCompleted={task?.task.is_every_employee_needs_to_complete}
      isTaskDataLoading={isLoading}
      isTaskLoading={isUpdating}
      formik={formik}
      handleCloseModal={handleCloseModal}
      isOpened={open}
      clientId={clientId}
      occurrenceStartDate={currentStartDate}
      occurrenceDueDate={currentDueDate}
      firstOccurrenceDate={originalStartDate}
    />
  );
};
